import { ContentKind } from '@mg/shared';
import type { AttachedAsset, EditorBlock } from './blocks';
import { insertMediaAtAnchor, withPostBlocks } from './thread';

/**
 * Put one finished image in the post, for "Save and return to post".
 *
 * `anchorKey` names the gallery the window was opened from (Generate media, in a media box). Without
 * one (Generate design, which has no box of its own) the image joins the post's first gallery, or
 * `tile` (an empty image block the caller made outside the state updater, which runs twice under
 * StrictMode) starts one at the end of the post, or of a thread's first post.
 *
 * The same rules as any other insert: the destination's image cap, and no photo beside a video on a
 * social post. A refused insert leaves the list as it was, and the empty tile is never left behind.
 */
export function attachToPost(
  prev: EditorBlock[],
  anchorKey: string | null,
  asset: AttachedAsset,
  imageLimit: number,
  tile: EditorBlock,
  opts: { social: boolean },
): EditorBlock[] {
  const insert = (blocks: EditorBlock[], key: string) =>
    insertMediaAtAnchor(blocks, key, [asset], imageLimit, { social: opts.social, recheckMixing: true });

  if (anchorKey && prev.some((b) => b.key === anchorKey)) return insert(prev, anchorKey);

  const firstPostKey = prev.find((b) => b.threadKey != null)?.threadKey ?? null;
  const gallery = prev.find(
    (b) => b.kind === ContentKind.IMAGE && (firstPostKey == null || b.threadKey === firstPostKey),
  );
  if (gallery) return insert(prev, gallery.key);

  const withTile =
    firstPostKey == null
      ? [...prev, tile]
      : withPostBlocks(prev, firstPostKey, (slice) => [...slice, { ...tile, threadKey: firstPostKey }]);
  if (withTile === prev) return prev;
  const next = insert(withTile, tile.key);
  // Still empty means the insert was refused: take the tile away again.
  return next.some((b) => b.key === tile.key && !b.assetId) ? prev : next;
}
